import { useMemo } from "react";
import { useTranslation } from "react-i18next";

type FooterLocale = "en" | "zh-Hant";

type FooterLogoLinks = Partial<Record<FooterLocale, string>> & {
  default: string;
};

type FooterLogoDefinition = {
  key: string;
  links: FooterLogoLinks;
};

type FooterLogo = {
  key: string;
  href: string;
  src: string;
};

const footerLogoDefinitions: FooterLogoDefinition[] = [
  {
    key: "logo1",
    links: {
      default: "https://www.hyab.gov.hk/tc/home/index.htm",
      en: "https://www.hyab.gov.hk/en/home/index.htm",
    },
  },
  {
    key: "logo2",
    links: {
      default: "https://www.cpce.gov.hk/main/tc/index.html",
      en: "https://www.cpce.gov.hk/main/en/index.html",
    },
  },
  {
    key: "logo3",
    links: {
      default: "https://www.basiclaw.gov.hk/tc/committee/index.html",
      en: "https://www.basiclaw.gov.hk/en/committee/index.html",
    },
  },
  {
    key: "logo4",
    links: {
      default: "https://www.facebook.com/cpce.events/",
    },
  },
  {
    key: "logo5",
    links: {
      default: "https://www.instagram.com/cpce_hk/",
    },
  },
];

const fallbackLocale: FooterLocale = "zh-Hant";

const resolveHref = (links: FooterLogoLinks, locale: FooterLocale): string =>
  links[locale] ?? links.default;

const footerLogos: Record<FooterLocale, FooterLogo[]> = {
  en: footerLogoDefinitions.map(({ key, links }) => ({
    key,
    href: resolveHref(links, "en"),
    src: `/basiclaw2025-26/assets/footer/${key}-en.png`
  })),
  "zh-Hant": footerLogoDefinitions.map(({ key, links }) => ({
    key,
    href: resolveHref(links, "zh-Hant"),
    src: `/basiclaw2025-26/assets/footer/${key}.png`
  }))
} satisfies Record<string, FooterLogo[]>;

export const Footer = () => {
  const { t, i18n } = useTranslation();
  const activeLogos = useMemo(() => {
    const availableLocales = Object.keys(footerLogos) as FooterLocale[];
    const locale = availableLocales.includes(i18n.language as FooterLocale)
      ? (i18n.language as FooterLocale)
      : fallbackLocale;
    return footerLogos[locale];
  }, [i18n.language]);

  return (
    <footer className="site-footer">
      <div className="site-footer__content">
        <p className="site-footer__heading">
          {t("footer.supportingOrganisations")}
        </p>
        <div className="site-footer__logos">
          {activeLogos.map(({ key, src, href }, idx) => {
            const alt = t("footer.logoAltTemplate", { index: idx + 1 });
            const isExternalLink = /^https?:\/\//i.test(href);

            return (
              <a
                key={key}
                href={href}
                className="site-footer__logo"
                target={isExternalLink ? "_blank" : undefined}
                rel={isExternalLink ? "noopener noreferrer" : undefined}
                aria-label={alt}
              >
                <img src={src} alt={alt} />
              </a>
            );
          })}
        </div>
      </div>
    </footer>
  );
};

